import ExpressionEngine from './ExpressionEngine';

/**
 * ColumnProcessor
 *
 * Processa e calcula valores de colunas para produtos.
 * Suporta colunas pre-definidas e customizadas com expressoes.
 */
class ColumnProcessor {
    constructor() {
        this.expressionEngine = new ExpressionEngine();
        this.cache = new Map();
    }

    /**
     * Processa array de produtos com colunas especificadas
     * @param {Array} products - Array de produtos
     * @param {Array} columns - Array de definicoes de colunas
     * @returns {Array} Produtos processados com valores das colunas
     */
    processColumns(products, columns) {
        if (!products || !Array.isArray(products)) {
            return [];
        }

        if (!columns || !Array.isArray(columns)) {
            return products;
        }

        return products.map((product) => {
            const processedProduct = { ...product };

            columns.forEach((column) => {
                try {
                    const value = this.calculateColumnValue(product, column);

                    // Armazenar valor calculado
                    if (column.type === 'custom') {
                        // Para colunas customizadas, usar o ID como chave
                        processedProduct[`custom_${column.id}`] = value;
                    }
                    // Colunas pre-definidas ja tem seus valores no produto
                } catch (error) {
                    console.error(`Erro ao calcular coluna ${column.name || column.label}:`, error);

                    // Em caso de erro, armazenar valor de erro
                    if (column.type === 'custom') {
                        processedProduct[`custom_${column.id}`] = null;
                        processedProduct[`custom_${column.id}_error`] = error.message;
                    }
                }
            });

            return processedProduct;
        });
    }

    /**
     * Calcula valor de uma coluna para um produto
     * @param {Object} product - Produto
     * @param {Object} column - Definicao da coluna
     * @returns {any} Valor calculado
     */
    calculateColumnValue(product, column) {
        if (column.type === 'predefined') {
            // Para colunas pre-definidas, retornar valor ja existente
            return this.getPredefinedValue(product, column.key);
        }

        if (column.type === 'custom') {
            // Para colunas customizadas, avaliar expressao
            return this.evaluateCustomColumn(product, column);
        }

        return null;
    }

    /**
     * Obtem valor de coluna pre-definida
     * @param {Object} product
     * @param {string} key
     * @returns {any}
     */
    getPredefinedValue(product, key) {
        // Tratamento especial para bestDay
        if (key === 'bestDay') {
            return product.bestDay;
        }

        // Retornar valor direto
        return product[key] !== undefined ? product[key] : 0;
    }

    /**
     * Avalia coluna customizada com expressao
     * @param {Object} product
     * @param {Object} column
     * @returns {any}
     */
    evaluateCustomColumn(product, column) {
        // Criar contexto para avaliacao
        const context = this.createProductContext(product);

        // Avaliar expressao
        const result = this.expressionEngine.evaluate(column.expression, context);

        // Formatar resultado se necessario
        return this.formatValue(result, column.format);
    }

    /**
     * Cria contexto de produto para avaliacao de expressoes
     * @param {Object} product
     * @returns {Object}
     */
    createProductContext(product) {
        return {
            itemId: product.itemId,
            title: product.title,
            dailySales: product.dailySales || new Map(),

            // Valores calculados
            today: product.today || 0,
            yesterday: product.yesterday || 0,
            day2: product.day2 || 0,
            day3: product.day3 || 0,
            last3d: product.last3d || 0,
            last5d: product.last5d || 0,
            last7d: product.last7d || 0,
            last15d: product.last15d || 0,
            last30d: product.last30d || 0,
            last60d: product.last60d || 0,
            last90d: product.last90d || 0,
            last180d: product.last180d || 0,
            last365d: product.last365d || 0,

            // Analise
            bestDay: product.bestDay || { date: null, quantity: 0 },
            average: product.average || 0,
            previousPeriod: product.previousPeriod || 0,
            twoPeriodsPast: product.twoPeriodsPast || 0,

            // Helpers
            getSalesForDay: (_daysAgo) => {
                // Implementar se necessario
                return 0;
            },
            getSalesForRange: (_startDaysAgo, _endDaysAgo) => {
                // Implementar se necessario
                return 0;
            },
        };
    }

    /**
     * Formata valor de acordo com o formato especificado
     * @param {any} value
     * @param {string} format
     * @returns {any}
     */
    formatValue(value, format) {
        if (value === null || value === undefined) {
            return null;
        }

        switch (format) {
            case 'number':
                return Number(value) || 0;

            case 'percentage': {
                const num = Number(value) || 0;
                return num; // Retornar numero, formatacao visual sera feita no componente
            }

            case 'currency':
                return Number(value) || 0;

            case 'string':
                return String(value);

            default:
                return value;
        }
    }

    /**
     * Obtem valor formatado para exibicao
     * @param {any} value
     * @param {string} format
     * @returns {string}
     */
    getDisplayValue(value, format) {
        if (value === null || value === undefined) {
            return '-';
        }

        switch (format) {
            case 'number':
                return Number(value).toLocaleString('pt-BR');

            case 'percentage': {
                const num = Number(value);
                return `${num.toFixed(1)}%`;
            }

            case 'currency':
                return Number(value).toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                });

            case 'string':
                return String(value);

            default:
                return String(value);
        }
    }

    /**
     * Limpa cache
     */
    clearCache() {
        this.cache.clear();
        this.expressionEngine.clearCache();
    }
}

export default ColumnProcessor;
